/**
 * Block 13 PR-G Phase 6.5G A8.6 (2026-05-25) — spec row filter для PDF rendering.
 *
 * Когда initial draft создаётся через manual seed (17 default позиций — entry
 * с Главной «Заполнить вручную» либо «+ Добавить мебель» → «Заполнить вручную»),
 * пользователь обычно заполняет только 5-7 нужных позиций и оставляет
 * остальные default rows нетронутыми. В готовый PDF попадают **только
 * заполненные** строки — это решение Aibek (visual verify round 5).
 *
 * **Filter criteria** (`isSpecRowFilled`):
 *   - `key === 'block_cost'` — **always** filled (block_cost row рендерится
 *     ВСЕГДА как итог по блоку, даже если cost=0).
 *   - Custom rows: хотя бы одно из (`type`, `brand`) — non-empty string ИЛИ
 *     `cost !== undefined` (cost=0 считается явно указанным).
 *
 * **`label` намеренно НЕ учитывается** — manual seed pre-fills label
 * («Столешница», «Фасады» и т.д.) для всех 17 default rows; если бы мы
 * считали row filled только по наличию label, скрыть пустые default позиции
 * было бы невозможно.
 *
 * **`cost === 0` vs `cost === undefined`:**
 *   - `cost === 0` → row filled (пользователь явно указал 0 — например
 *     бесплатная установка).
 *   - `cost === undefined` → row не filled (default factory value).
 *
 * **PR-C4 round-2 (ADR-0038, 2026-05-31) — `isSpecRowVisibleInPdf`:** PDF
 * **зеркалит форму** (`SpecBlockEditor` рендерит все `customRows`). Строка
 * попадает в PDF, если у неё непустой `label` ИЛИ она `isSpecRowFilled`
 * (Тип/Бренд/cost). Прячутся **только полностью пустые** строки (label И type
 * И brand пусты, cost undefined) — бессмысленные пустые «болванки» на печати.
 * Round-5 (A8.6 «прятать name-only из PDF») **отменён по запросу Aibek**:
 * ручные шаблонные строки вроде «Столешница» без Тип/Бренд теперь показываются.
 * `isSpecRowFilled` остаётся unchanged как базовый предикат «заполненности».
 *
 * Reference:
 *   - ADR-0025 Amendment A8.6 (исходный PDF filter — round-5, отменён round-2)
 *   - ADR-0038 round-2 (PDF зеркалит форму; source-маркер откатан)
 *   - `contractPdfDrawing.drawSpecBlocks` (consumer)
 */

import type { ContractSpecRow } from '@pomoshnik/shared';

export function isSpecRowFilled(row: ContractSpecRow): boolean {
  // block_cost — always rendered как footer per-block table.
  if (row.key === 'block_cost') return true;

  // Custom row: filled если хотя бы одно из type/brand non-empty ИЛИ cost defined.
  const typeFilled = typeof row.type === 'string' && row.type.trim() !== '';
  const brandFilled = typeof row.brand === 'string' && row.brand.trim() !== '';
  const costFilled = row.cost !== undefined;

  return typeFilled || brandFilled || costFilled;
}

/**
 * PR-C4 round-2 (ADR-0038) — PDF-видимость строки спецификации (зеркало формы).
 *
 * Строка попадает в PDF если у неё непустой `label` ИЛИ она `isSpecRowFilled`
 * (block_cost ИЛИ Тип/Бренд/cost). Прячутся только полностью пустые строки
 * (label И type И brand пусты, cost undefined). Зеркалит `SpecBlockEditor`,
 * который показывает все `customRows`. Используется в `drawSpecBlocks` вместо
 * `isSpecRowFilled`.
 */
export function isSpecRowVisibleInPdf(row: ContractSpecRow): boolean {
  return isSpecRowFilled(row) || row.label.trim() !== '';
}
